'use client'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useLanguage } from '@/lib/context/LanguageContext'
import SocialIconLink from '@/components/ui/SocialIconLink'
import InlineIcon from '@/components/ui/InlineIcon'
import { getSocials } from '@/lib/data/socials'
import type { Lang } from '@/lib/i18n'

// Render Latin digits in the locale's numerals (Persian ۰-۹, Arabic ٠-٩) so a
// phone/number label matches the surrounding RTL copy. Non-digits pass through.
const DIGITS: Partial<Record<Lang, string>> = {
  fa: '۰۱۲۳۴۵۶۷۸۹',
  ar: '٠١٢٣٤٥٦٧٨٩',
}
function localizeDigits(value: string, lang: Lang): string {
  const set = DIGITS[lang]
  if (!set) return value
  return value.replace(/[0-9]/g, (d) => set[Number(d)])
}

/**
 * Footer — three-column reference grid: brand, in-page nav, contact.
 * Below it: regulatory disclaimer, then a baseline row with copyright,
 * social icons, and back-to-top. Direction flips with the document
 * <html dir>; the contact and social rows stay LTR so email/phone/
 * address read correctly inside an RTL layout.
 */
export default function Footer() {
  const { t, lang } = useLanguage()
  const year = new Date().getFullYear()
  // Homepage section anchors (#top, #products …) only exist on '/'. From any
  // other route, prefix with '/' so they navigate home and then scroll.
  const pathname = usePathname()
  const anchor = (hash: string) => (pathname === '/' ? hash : `/${hash}`)
  // TEMP (revert this week): social media removed
  // const socials = getSocials(lang)

  return (
    <footer className="border-t border-surface/10 bg-ink">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <a href={anchor('#top')} aria-label="MRN — Mah Rokh Naz — back to top" className="focus-ring-light inline-block">
              {/* Full master lockup: monogram + red ring + subtitle, in the
                  white-on-dark treatment for the ink footer. */}
              <img
                src="/brand/mrn-logo-full-ondark.svg"
                alt="MRN — Mah Rokh Naz"
                className="h-20 w-auto"
                width={262}
                height={200}
                loading="lazy"
                decoding="async"
              />
            </a>
            <p className="mt-4 max-w-sm text-sm text-surface/65 leading-relaxed">{t.footer.tagline}</p>
            {/* eNAMAD trust seal — official embed from enamad.ir/logohelp for
                id=756904. Their docs require the code UNALTERED (server-side
                referrer + id/code validation; altered embeds stop rendering),
                so the <a>/<img> attributes stay exactly as issued — JSX casing
                only. All presentation lives on this wrapper: a light card so
                the seal reads on the ink footer, image capped via descendant
                selector. NOTE: renders a placeholder off-domain (localhost) —
                the real seal only serves to mrnmed.com referrers. */}
            <div className="mt-6 w-fit rounded-xl bg-surface p-2 [&_img]:max-h-24 [&_img]:w-auto">
              <a
                referrerPolicy="origin"
                target="_blank"
                href="https://trustseal.enamad.ir/?id=756904&Code=Oe1R4sAA6lX4aCBuE8OFB7L3tIUd3lJg"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  referrerPolicy="origin"
                  src="https://trustseal.enamad.ir/logo.aspx?id=756904&Code=Oe1R4sAA6lX4aCBuE8OFB7L3tIUd3lJg"
                  alt=""
                  style={{ cursor: 'pointer' }}
                  {...{ code: 'Oe1R4sAA6lX4aCBuE8OFB7L3tIUd3lJg' }}
                />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-surface/60 font-medium">
              {t.footer.navHeading}
            </h3>
            <ul className="mt-4 flex flex-col gap-2 text-sm text-surface/70">
              <li>
                <a href={anchor('#products')} className="focus-ring-light hover:text-surface transition-colors">
                  {t.nav.products}
                </a>
              </li>
              <li>
                <a href={anchor('#why')} className="focus-ring-light hover:text-surface transition-colors">
                  {t.nav.why}
                </a>
              </li>
              {/* TEMP (revert this week): markets + FAQ sections disabled
              <li>
                <a href={anchor('#markets')} className="focus-ring-light hover:text-surface transition-colors">
                  {t.nav.markets ?? 'Markets'}
                </a>
              </li>
              <li>
                <a href={anchor('#faq')} className="focus-ring-light hover:text-surface transition-colors">
                  {t.nav.faq}
                </a>
              </li> */}
              {/* TEMP (revert this week): journal hidden while it has no published posts
              <li>
                <Link href="/journal" className="focus-ring-light hover:text-surface transition-colors">
                  {t.nav.journal ?? 'Journal'}
                </Link>
              </li> */}
              <li>
                <a href={anchor('#contact')} className="focus-ring-light hover:text-surface transition-colors">
                  {t.nav.contact}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-surface/60 font-medium">
              {t.footer.contactHeading}
            </h3>
            <ul className="mt-4 flex flex-col gap-2 text-sm text-surface/70">
              <li>
                <a
                  href={`mailto:${t.contact.directEmail}`}
                  className="flex items-center gap-2 focus-ring-light hover:text-surface transition-colors"
                >
                  <InlineIcon src="/icons/contact/email.svg" />
                  {/* Isolate just the Latin value as LTR — keep the row itself in
                      page direction so its icon leads on the same side as the
                      phone/address icons (otherwise the column's icons go ragged
                      under RTL). */}
                  <bdi dir="ltr">{t.contact.directEmail}</bdi>
                </a>
              </li>
              <li className="flex items-center gap-2 text-surface/65">
                <InlineIcon src="/icons/contact/phone.svg" />
                {/* Both lines, kept LTR so the digits read correctly under RTL.
                    The tel: href stays Latin ASCII (required to dial); only the
                    visible label localises to Persian/Arabic digits to match the
                    address line. */}
                <span dir="ltr" className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {[
                    { href: 'tel:+982126876834', label: '021-26876834' },
                    { href: 'tel:+982126876793', label: '021-26876793' },
                  ].map((p) => (
                    <a
                      key={p.href}
                      href={p.href}
                      // Arabic-Indic digits are bidi class AN, so the hyphen
                      // reorders the groups (021-268… → 268…-021). Override to
                      // strict LTR so the number reads as written in every locale.
                      style={{ direction: 'ltr', unicodeBidi: 'isolate-override' }}
                      className="focus-ring-light hover:text-surface transition-colors"
                    >
                      {localizeDigits(p.label, lang)}
                    </a>
                  ))}
                </span>
              </li>
              <li className="flex items-center gap-2 text-surface/65">
                <InlineIcon src="/icons/contact/location.svg" />
                {t.contact.address}
              </li>
            </ul>
          </div>
        </div>

        <p className="mt-12 max-w-3xl text-xs text-surface/60 leading-relaxed">
          {t.footer.disclaimer}
        </p>

        <div className="mt-8 flex flex-col gap-5 md:flex-row md:items-center md:justify-between text-xs text-surface/65">
          {/* Bidi-isolate the LTR brand cluster (©, year, "MRN") from the
              localized rights text. The current copy happens to resolve cleanly
              under dir="rtl", but isolating each run keeps it bulletproof if the
              rights string ever opens with a Latin token or the format changes —
              the em-dash stays put between the two. Identical in LTR English. */}
          <span>
            <bdi dir="ltr">© {localizeDigits(String(year), lang)} MRN</bdi> — <bdi>{t.footer.rights}</bdi>
          </span>

          <div className="flex items-center gap-5">
            {/* TEMP (revert this week): social media icons removed */}
            <a href={anchor('#top')} className="focus-ring-light hover:text-surface transition-colors">
              {t.footer.backToTop} ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
